import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router-dom'
import { DotLoader } from 'react-spinners'
import { useAuth } from '@/context/AuthContext'
import { useAdminAccount } from '@/context/AdminAccountContext'

// ログイン関連（即座に読み込み）
import AuthLogin from '@/views/auth/login'
import FirstLogin from '@views/auth/first-login'

// レイアウト（即座に読み込み）
import MainLayout from '@layouts/MainLayout'

// エラーページ（lazy loading）
const Error400 = lazy(() => import('@views/error/400'))
const Error401 = lazy(() => import('@views/error/401'))
const Error403 = lazy(() => import('@views/error/403'))
const Error404 = lazy(() => import('@views/error/404'))
const Error408 = lazy(() => import('@views/error/408'))
const Error500 = lazy(() => import('@views/error/500'))

// ダッシュボード（lazy loading）
const Dashboard = lazy(() => import('@views/dashboards'))

// 学校管理（lazy loading）
const PreRegister = lazy(() => import('@views/school/pre-register'))
const PreRegisterDetail = lazy(() => import('@views/school/pre-register-detail'))
const SchoolList = lazy(() => import('@views/school/school-list'))
const SchoolListDetail = lazy(() => import('@views/school/school-detail'))

// 案件管理（lazy loading）
const ActiveProjectList = lazy(() => import('@views/projects/active-project-list'))
const ActiveProjectListDetail = lazy(() => import('@/views/projects/project-detail'))

// お問い合わせ管理（lazy loading）
const ActiveContactList = lazy(() => import('@views/contact/active-contact-list'))
const ContactDetail = lazy(() => import('@views/contact/contact-detail'))

// 請求管理（lazy loading）
const BillingCurrent = lazy(() => import('@/views/billing'))

// アカウント管理（lazy loading）
const AccountList = lazy(() => import('@/views/account/account-list'))
const MyAccount = lazy(() => import('@/views/account/my_account'))
const ManualPage = lazy(() => import('@views/manual'))

// 認証関連ルート
export const authRoutes: RouteObject[] = [
  {
    path: '/login',
    element: <AuthLogin />,
  },
  {
    path: '/first-login',
    element: <FirstLogin />,
  },
]

// エラーページルート
export const errorRoutes: RouteObject[] = [
  {
    path: '/error/400',
    element: <Error400 />,
  },
  {
    path: '/error/401',
    element: <Error401 />,
  },
  {
    path: '/error/403',
    element: <Error403 />,
  },
  {
    path: '/error/404',
    element: <Error404 />,
  },
  {
    path: '/error/408',
    element: <Error408 />,
  },
  {
    path: '/error/500',
    element: <Error500 />,
  },
]

// ダッシュボードルート（メニューあり）
export const dashboardRoutes: RouteObject[] = [
  {
    path: '/dashboards',
    element: <Dashboard />,
  },
]

// 学校管理ルート
export const schoolRoutes: RouteObject[] = [
  {
    path: '/school/pre-register',
    element: <PreRegister />,
  },
  {
    path: '/school/pre-register/detail/:id',
    element: <PreRegisterDetail />,
  },
  {
    path: '/school/school-list',
    element: <SchoolList />,
  },
  {
    path: '/school/school-list/detail/:id',
    element: <SchoolListDetail />,
  },
]

// 案件管理ルート
export const projectRoutes: RouteObject[] = [
  {
    path: '/projects/active-list',
    element: <ActiveProjectList />,
  },
  {
    path: '/projects/active-list/detail/:schoolId/:projectId',
    element: <ActiveProjectListDetail />,
  },
]

// お問い合わせ管理ルート
export const contactRoutes: RouteObject[] = [
  {
    path: '/contact/active-list',
    element: <ActiveContactList />,
  },
  {
    path: '/contact/detail/:schoolId/:contactId',
    element: <ContactDetail />,
  },
]

// 請求管理ルート
export const billingRoutes: RouteObject[] = [
  {
    path: '/billing/current',
    element: <BillingCurrent />,
  },
]

// アカウント管理ルート
export const accountRoutes: RouteObject[] = [
  {
    path: '/account',
    element: <AccountList />,
  },
  {
    path: '/account/my_account',
    element: <MyAccount />,
  },
]

// マニュアルルート
export const manualRoutes: RouteObject[] = [
  {
    path: '/manual',
    element: <ManualPage />,
  },
]

// ログイン前のルート
export const notSignInRoutes: RouteObject[] = [
  {
    path: '/',
    element: <RootRedirect />,
  },
  ...authRoutes,
  ...errorRoutes,
]

// サーバーエラー表示用フォールバック（ルート errorElement 用）
const Error500Fallback = () => (
  <div className="loading-overlay">
    <DotLoader color="var(--irogami-primary)" size={40} speedMultiplier={1} />
  </div>
)

function RootRedirect() {
  const { user, loading: authLoading } = useAuth()
  const { adminAccount, isLoading: adminAccountLoading } = useAdminAccount()

  if (authLoading || adminAccountLoading) {
    return <Error500Fallback />
  }

  if (user && adminAccount) {
    if (!adminAccount.lastLoginAt) {
      return <Navigate to="/first-login" replace />
    }
    return <Navigate to="/dashboards" replace />
  }

  return <Navigate to="/login" replace />
}

// ログイン後のルート（メニューありレイアウトでラップ）
export const signInRoutes: RouteObject[] = [
  {
    element: <MainLayout />,
    errorElement: (
      <Suspense fallback={<Error500Fallback />}>
        <Error500 />
      </Suspense>
    ),
    children: [
      ...dashboardRoutes,
      ...schoolRoutes,
      ...projectRoutes,
      ...contactRoutes,
      ...billingRoutes,
      ...accountRoutes,
      ...manualRoutes,
    ],
  },
]

// 全体ルート（ルート階層で errorElement を指定し、レンダー時のエラーで 500 を表示）
export const routes: RouteObject[] = [
  {
    element: <Outlet />,
    errorElement: (
      <Suspense fallback={<Error500Fallback />}>
        <Error500 />
      </Suspense>
    ),
    children: [
      ...notSignInRoutes,
      ...signInRoutes,
      // 存在しないパスにアクセスした場合のフォールバックルート
      {
        path: '*',
        element: <Error404 />,
      },
    ],
  },
]

export const router = createBrowserRouter(routes)

